import React from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import cn from 'classnames';
import _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faTimes } from '@fortawesome/pro-light-svg-icons';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import './ShopTabs.scss';

import { hasProductIdsPromoted, isShopper } from '../../../Helpers/user_helpers';

const ShopTabs = props => {
  const { user, isYourShop, activeRefinementObjects } = props;
  const { activeCurator, activeCuratorGroup, activeBrands, activeCategory, activeDepartment } = activeRefinementObjects || {};

  const tabs = [
    {
      label: 'Latest Finds',
      mobileLabel: 'Latest',
      onClick: () => props.goToTab('latest'),
      isActive: props.tab === 'latest',
      isHidden: !!props.query
    },
    {
      value: `collections`,
      onClick: () => props.goToTab('collections'),
      label: isYourShop && isShopper(user) ? 'Wishlists' : 'Collections',
      mobileLabel: isYourShop && isShopper(user) ? 'Wishlists' : 'Collections',
      isActive: props.tab === 'collections',
      sublabel: 'Curated Lists.',
      isHidden: !props.isCuratedShop || !!props.query
    },
    {
      label: 'Most Popular',
      mobileLabel: 'Popular',
      onClick: () => props.goToTab('popular'),
      isActive: props.tab === 'popular',
      isHidden: (isShopper(user) && isYourShop) || !!props.query
    },
    {
      value: 'consults',
      label: 'Consults',
      onClick: () => props.goToTab('consults'),
      isActive: props.tab === 'consults',
      mobileLabel: 'Consults',
      sublabel: '1 on 1 consultations.',
      isHidden: !activeCurator || !activeCurator.showConsultsTabInShop || !!props.query
    },
    {
      label: 'For You',
      mobileLabel: 'For You',
      onClick: () => props.goToTab('foryou'),
      isActive: props.tab === 'foryou',
      isHidden: isYourShop || !!props.query,
      isDisabled: !hasProductIdsPromoted(user),
      isDisabledWarning: !hasProductIdsPromoted(user) ? 'Heart a few products to get personalized recommendations' : null
    }
  ].filter(c => !c.isHidden);

  // Search Behavior
  const [searchQuery, setSearchQuery] = React.useState('');
  const [isInSearchMode, setIsInSearchMode] = React.useState(false);
  const toggleSearchMode = () => setIsInSearchMode(!isInSearchMode);
  const clearSearch = () => {
    setSearchQuery('');
    setIsInSearchMode(false);
  };
  const blurSearch = () => !searchQuery && setIsInSearchMode(false);
  const closeSearchMode = () => setIsInSearchMode(false);

  const submitSearch = e => {
    e?.preventDefault();
    props.setQuery(searchQuery);
    clearSearch();
  };

  // UI Handling
  const additionalClasses = {
    'in-search-mode': isInSearchMode
  };

  return (
    <div className='shop-tabs-outer-container'>
      <div className={cn('shop-tabs', additionalClasses)}>
        <div className='search-container'>
          {isInSearchMode ? (
            <div className='is-searching-container-outer'>
              <form onSubmit={submitSearch} className='is-searching-container'>
                <div className='search-input-container has-actions'>
                  <div className='search-icon'>
                    <FontAwesomeIcon icon={faSearch} />
                  </div>
                  <input
                    autoFocus
                    onBlur={blurSearch} // REMOVE BEFORE LANDING
                    placeholder={
                      activeCurator
                        ? `Search ${activeCurator.name}${_.last(activeCurator.name) === 's' ? "'" : "'s"} Shop`
                        : activeCuratorGroup
                        ? `Search ${activeCuratorGroup.title}${_.last(activeCuratorGroup.title) === 's' ? "'" : "'s"} Finds`
                        : activeBrands?.length
                        ? `Search ${activeBrands[0].name} Brands`
                        : activeCategory
                        ? `Search ${activeCategory.name}`
                        : activeDepartment
                        ? `Search ${activeDepartment.name}`
                        : 'Search Shop'
                    }
                    type='text'
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                  />
                  <div className={cn('submit-icon', { active: !!searchQuery })} onClick={submitSearch}>
                    <FontAwesomeIcon icon={faArrowRight} />
                  </div>
                </div>
                <div className={cn('clear-icon')} onClick={closeSearchMode}>
                  <FontAwesomeIcon icon={faTimes} />
                </div>
              </form>
            </div>
          ) : (
            <div onClick={toggleSearchMode} className={cn('search-button', { 'search-again': !!props.query })}>
              <FontAwesomeIcon icon={faSearch} />
            </div>
          )}
        </div>
        {props.query && !isInSearchMode && (
          <div onClick={() => props.setQuery('')} className='shop-tab active clearable'>
            <div>{props.query}</div>
            <div>
              <FontAwesomeIcon icon={faTimes} />
            </div>
          </div>
        )}
        {!isInSearchMode &&
          tabs.map((tab, idx) => {
            const click = () => (tab.isActive ? null : tab.isDisabled ? window.ALERT.warn(tab.isDisabledWarning) : tab.onClick());
            return (
              <div className={cn('shop-tab', { active: tab.isActive })} key={tab.label + idx} onClick={click}>
                {tab.label}
              </div>
            );
          })}
      </div>
    </div>
  );
};

ShopTabs.propTypes = {
  user: PropTypes.object.isRequired,
  isYourShop: PropTypes.bool.isRequired,
  hasSearchQuery: PropTypes.bool.isRequired,
  isUnfilteredState: PropTypes.bool.isRequired,
  activeRefinementObjects: PropTypes.object.isRequired,
  goToTab: PropTypes.func.isRequired,
  tab: PropTypes.string.isRequired
};

const mapStateToProps = state => {
  const { user } = state;
  return { user };
};

export default connect(mapStateToProps, {
  // functions go here
})(ShopTabs);
